if (!customElements.get('product-video-gallery')) {
  customElements.define(
    'product-video-gallery',
    class ProductVideoGallery extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.track = this.querySelector('[data-product-video-track]');
        this.navigation = this.querySelector('[data-product-video-navigation]');
        this.previousButton = this.querySelector('[data-product-video-previous]');
        this.nextButton = this.querySelector('[data-product-video-next]');
        this.modal = this.querySelector('[data-product-video-modal]');
        this.dialog = this.querySelector('.product-video-gallery__dialog');
        this.player = this.querySelector('[data-product-video-player]');
        this.youtubePlayer = this.querySelector('[data-product-video-youtube-player]');
        this.closeButton = this.querySelector('.product-video-gallery__close');
        this.modalPreviousButton = this.querySelector('[data-product-video-modal-previous]');
        this.modalNextButton = this.querySelector('[data-product-video-modal-next]');
        this.triggers = Array.from(this.querySelectorAll('[data-product-video-trigger]'));
        this.currentIndex = -1;
        this.lastTrigger = null;

        this.onScroll = this.updateNavigation.bind(this);
        this.onResize = () => {
          this.updateNavigation();
          this.updateDialogSize();
        };
        this.onKeydown = this.handleKeydown.bind(this);
        this.onLoadedMetadata = this.updateNativeAspectRatio.bind(this);

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', () => this.openVideo(trigger));
        });
        this.querySelectorAll('[data-product-video-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeVideo());
        });

        this.previousButton?.addEventListener('click', () => this.scroll(-1));
        this.nextButton?.addEventListener('click', () => this.scroll(1));
        this.modalPreviousButton?.addEventListener('click', () => this.navigateVideo(-1));
        this.modalNextButton?.addEventListener('click', () => this.navigateVideo(1));
        this.track?.addEventListener('scroll', this.onScroll, { passive: true });
        this.player?.addEventListener('loadedmetadata', this.onLoadedMetadata);
        window.addEventListener('resize', this.onResize);
        document.addEventListener('keydown', this.onKeydown);
        if (this.modal) document.body.appendChild(this.modal);
        this.updateNavigation();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.player?.removeEventListener('loadedmetadata', this.onLoadedMetadata);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('keydown', this.onKeydown);
        this.closeVideo(false);
        this.modal?.remove();
        this.initialized = false;
      }

      scroll(direction) {
        if (!this.track) return;
        const firstCard = this.track.querySelector('.product-video-gallery__item');
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const distance = (firstCard?.getBoundingClientRect().width || this.track.clientWidth) + gap;
        this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      }

      updateNavigation() {
        if (!this.track || !this.navigation) return;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const overflowing = maxScroll > 2;
        this.navigation.hidden = !overflowing;
        if (!overflowing) return;
        this.previousButton.disabled = this.track.scrollLeft <= 2;
        this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      }

      openVideo(trigger) {
        const index = this.triggers.indexOf(trigger);
        if (index < 0 || !this.player || !this.modal) return;

        this.player.muted = this.dataset.startMuted !== 'false';
        this.modal.hidden = false;
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('product-video-gallery-open');
        this.loadVideo(index);
        this.closeButton?.focus();
      }

      loadVideo(index) {
        const trigger = this.triggers[index];
        const source = trigger?.dataset.videoSrc;
        const type = trigger?.dataset.videoType || 'video';
        if (!source || !this.player || !this.youtubePlayer) return;

        // 弹窗切换视频时，仅加载当前选中的资源。
        this.currentIndex = index;
        this.lastTrigger = trigger;
        this.resetPlayers();
        this.updateAspectRatio(trigger.dataset.videoAspectRatio);

        if (type === 'youtube') {
          const separator = source.includes('?') ? '&' : '?';
          const muted = this.dataset.startMuted !== 'false' ? '&mute=1' : '';
          this.youtubePlayer.hidden = false;
          this.youtubePlayer.title = trigger.dataset.videoTitle || 'Product video';
          // 弹窗已经提供独立尺寸控制，隐藏无效的 YouTube 全屏按钮。
          this.youtubePlayer.src = `${source}${separator}autoplay=1&rel=0&fs=0${muted}`;
        } else {
          this.player.hidden = false;
          this.player.muted = this.dataset.startMuted !== 'false';
          this.player.src = source;
          this.player.poster = trigger.dataset.videoPoster || '';
          this.player.setAttribute('aria-label', trigger.dataset.videoTitle || 'Product video');
          this.player.load();
          this.player.play().catch(() => {});
        }
        this.updateModalNavigation();
      }

      updateAspectRatio(value) {
        if (!this.dialog) return;
        const ratio = Number.parseFloat(value);
        this.currentAspectRatio = ratio > 0 ? ratio : 16 / 9;
        this.dialog.style.setProperty('--product-video-modal-aspect-ratio', this.currentAspectRatio);
        this.updateDialogSize();
      }

      updateDialogSize() {
        if (!this.dialog || !this.currentAspectRatio) return;
        // 移动端为框外退出按钮预留顶部空间，再按可用高度反算最大宽度。
        const verticalSpace = window.innerWidth <= 749 ? 68 : 40;
        const widthByHeight = Math.max(0, window.innerHeight - verticalSpace) * this.currentAspectRatio;
        this.dialog.style.setProperty('--product-video-modal-vertical-space', `${verticalSpace}px`);
        this.dialog.style.setProperty('--product-video-modal-width-by-height', `${widthByHeight}px`);
      }

      updateNativeAspectRatio() {
        if (this.player.hidden || !this.player.videoWidth || !this.player.videoHeight) return;
        // MP4 元数据加载完成后，使用视频真实宽高更新弹窗比例。
        this.updateAspectRatio(this.player.videoWidth / this.player.videoHeight);
      }

      resetPlayers() {
        // 清理未使用的播放器，切换或关闭后不继续播放和占用网络。
        this.player.pause();
        this.player.removeAttribute('src');
        this.player.removeAttribute('poster');
        this.player.load();
        this.player.hidden = true;
        this.youtubePlayer.removeAttribute('src');
        this.youtubePlayer.hidden = true;
      }

      navigateVideo(direction) {
        const nextIndex = this.currentIndex + direction;
        if (nextIndex < 0 || nextIndex >= this.triggers.length) return;
        this.loadVideo(nextIndex);
      }

      updateModalNavigation() {
        const hasMultipleVideos = this.triggers.length > 1;
        if (this.modalPreviousButton) {
          this.modalPreviousButton.hidden = !hasMultipleVideos;
          this.modalPreviousButton.disabled = this.currentIndex <= 0;
        }
        if (this.modalNextButton) {
          this.modalNextButton.hidden = !hasMultipleVideos;
          this.modalNextButton.disabled = this.currentIndex >= this.triggers.length - 1;
        }
      }

      closeVideo(restoreFocus = true) {
        if (!this.player || !this.modal || this.modal.hidden) return;
        this.resetPlayers();
        this.currentIndex = -1;
        this.modal.hidden = true;
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('product-video-gallery-open');
        if (restoreFocus) this.lastTrigger?.focus();
      }

      handleKeydown(event) {
        if (!this.modal || this.modal.hidden) return;
        if (event.key === 'Escape') this.closeVideo();
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigateVideo(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigateVideo(1);
        }
      }
    }
  );
}
